<template>
  <div id="carry-us-form">
    <div v-if="submitted">
      <h2 class="text-primary text-h1 mb-8 mt-60px text-center">
        Thank You for your Application!
      </h2>
      <p class="text-center">
        Someone from our education team will be contacting you shortly.
      </p>
    </div>
    <form @submit.prevent="submit" v-if="!submitted" class="text-left">
      <h2 class="content-title mb-3 pb-3">
        R+Co Regional Educator Application
      </h2>

      <!-- 1. Name -->
      <div class="flex flex-wrap mt-2 -mx-3">
        <div class="w-full px-3">
          <base-input
              tabindex="1"
              :input-class="'text-black'"
              placeholder="Enter your name"
              name="name"
              type="text"
              v-model="formData.name"
              @blur="$v.formData.name.$touch()"
              :validations="[
              {
                condition: $v.formData.name.$error && !$v.formData.name.required,
                text: validateMessage('required', { attr: 'Name'})
              },
              {
                condition: !$v.formData.name.minLength,
                text: validateMessage('minLength', { attr: 'Name', min: 2 })
              }
            ]"
          >
            Name <span>*</span>
          </base-input>
        </div>
      </div>

      <!-- 2. Salon -->
      <div class="flex flex-wrap mt-2 -mx-3">
        <div class="w-full px-3">
          <base-input
              tabindex="2"
              :input-class="'text-black'"
              placeholder="Enter your salon"
              name="salon"
              type="text"
              v-model="formData.salon"
              @blur="$v.formData.salon.$touch()"
              :validations="[
              {
                condition: $v.formData.salon.$error && !$v.formData.salon.required,
                text: validateMessage('required', { attr: 'Salon'})
              }
            ]"
          >
            Salon <span>*</span>
          </base-input>
        </div>
      </div>

      <!-- 3. Address -->
      <div class="flex flex-wrap mt-2 -mx-3">
        <div class="w-full px-3">
          <base-input
              tabindex="3"
              :input-class="'text-black'"
              placeholder="Street address"
              autocomplete="address-line1"
              name="address"
              type="text"
              v-model="formData.address"
              @blur="$v.formData.address.$touch()"
              :validations="[
              {
                condition: $v.formData.address.$error && !$v.formData.address.required,
                text: validateMessage('required', { attr: 'Address'})
              }
            ]"
          >
            Address <span>*</span>
          </base-input>
        </div>
      </div>
      <div class="flex flex-wrap mt-2 -mx-3">
        <div class="w-full px-3">
          <base-input
              tabindex="4"
              :input-class="'text-black'"
              placeholder="Apt, suite, unit, etc. (optional)"
              autocomplete="address-line2"
              name="address2"
              type="text"
              v-model="formData.address2"
          >
            Address Line 2
          </base-input>
        </div>
      </div>
      <div class="flex flex-wrap mt-2 -mx-3">
        <div class="w-full md:w-1/2 px-3 mb-6 md:mb-0">
          <base-input
              tabindex="5"
              :input-class="'text-black'"
              placeholder="City"
              autocomplete="address-level2"
              name="city"
              type="text"
              v-model="formData.city"
              @blur="$v.formData.city.$touch()"
              :validations="[
              {
                condition: $v.formData.city.$error && !$v.formData.city.required,
                text: validateMessage('required', { attr: 'City'})
              }
            ]"
          >
            City <span>*</span>
          </base-input>
        </div>
        <div class="w-full md:w-1/2 px-3">
          <base-input
              tabindex="6"
              :input-class="'text-black'"
              placeholder="State / Province"
              autocomplete="address-level1"
              name="state"
              type="text"
              v-model="formData.state"
              @blur="$v.formData.state.$touch()"
              :validations="[
              {
                condition: $v.formData.state.$error && !$v.formData.state.required,
                text: validateMessage('required', { attr: 'State'})
              }
            ]"
          >
            State / Province <span>*</span>
          </base-input>
        </div>
      </div>
      <div class="flex flex-wrap mt-2 -mx-3">
        <div class="w-full md:w-1/2 px-3 mb-6 md:mb-0">
          <base-input
              tabindex="7"
              :input-class="'text-black'"
              placeholder="Zip / Postal code"
              autocomplete="postal-code"
              name="zip"
              type="text"
              v-model="formData.zip"
              @blur="$v.formData.zip.$touch()"
              :validations="[
              {
                condition: $v.formData.zip.$error && !$v.formData.zip.required,
                text: validateMessage('required', { attr: 'Zip code'})
              }
            ]"
          >
            Zip / Postal Code <span>*</span>
          </base-input>
        </div>
      </div>

      <!-- 4. Referred by -->
      <div class="flex flex-wrap mt-2 -mx-3">
        <div class="w-full px-3">
          <base-input
              tabindex="8"
              :input-class="'text-black'"
              placeholder="Enter who referred you"
              name="referred_by"
              type="text"
              v-model="formData.referred_by"
              @blur="$v.formData.referred_by.$touch()"
              :validations="[
              {
                condition: $v.formData.referred_by.$error && !$v.formData.referred_by.required,
                text: validateMessage('required', { attr: 'Referred by'})
              }
            ]"
          >
            Referred by <span>*</span>
          </base-input>
        </div>
      </div>

      <!-- 5. Phone number -->
      <div class="flex flex-wrap mt-2 -mx-3">
        <div class="w-full md:w-1/2 px-3 mb-6 md:mb-0">
          <base-input
              tabindex="9"
              :input-class="'text-black'"
              placeholder="Enter your phone number"
              autocomplete="tel"
              name="phone"
              type="text"
              v-model="formData.phone"
              @blur="$v.formData.phone.$touch()"
              :validations="[
              {
                condition: $v.formData.phone.$error && !$v.formData.phone.required,
                text: validateMessage('required', { attr: 'Phone number'})
              },
              {
                condition: !$v.formData.phone.minLength,
                text: validateMessage('minLength', { attr: 'Phone number', min: 5 })
              },
              {
                condition: !$v.formData.phone.isPhone,
                text: validateMessage('isPhone', { attr: 'Phone number' })
              }
            ]"
          >
            Phone number <span>*</span>
          </base-input>
        </div>

        <!-- 6. Email -->
        <div class="w-full md:w-1/2 px-3">
          <base-input
              tabindex="10"
              :input-class="'text-black'"
              placeholder="Enter your email"
              autocomplete="email"
              name="email"
              type="email"
              v-model="formData.email"
              @blur="$v.formData.email.$touch()"
              :validations="[
              {
                condition: $v.formData.email.$error && !$v.formData.email.required,
                text: validateMessage('required', { attr: 'Email'})
              },
              {
                condition: !$v.formData.email.email,
                text: validateMessage('email', { attr: 'Email' })
              }
            ]"
          >
            Email <span>*</span>
          </base-input>
        </div>
      </div>

      <!-- 7. Instagram -->
      <div class="flex flex-wrap mt-2 -mx-3">
        <div class="w-full px-3">
          <base-input
              tabindex="11"
              :input-class="'text-black'"
              placeholder="Enter your Instagram handle"
              name="instagram"
              type="text"
              v-model="formData.instagram"
              @blur="$v.formData.instagram.$touch()"
              :validations="[
              {
                condition: $v.formData.instagram.$error && !$v.formData.instagram.required,
                text: validateMessage('required', { attr: 'Instagram handle'})
              }
            ]"
          >
            Instagram social media handle <span>*</span>
          </base-input>
        </div>
      </div>

      <!-- 8. Salon owner approved -->
      <div class="flex flex-wrap mt-2 -mx-3">
        <div class="w-full px-3">
          <base-input
              tabindex="12"
              :input-class="'text-black'"
              placeholder="Enter your answer"
              name="salon_owner_approved"
              type="text"
              v-model="formData.salon_owner_approved"
              @blur="$v.formData.salon_owner_approved.$touch()"
              :validations="[
              {
                condition: $v.formData.salon_owner_approved.$error && !$v.formData.salon_owner_approved.required,
                text: validateMessage('required', { attr: 'This field'})
              }
            ]"
          >
            Has your salon owner approved you applying for this role? <span>*</span>
          </base-input>
        </div>
      </div>

      <!-- 9. Availability -->
      <div class="flex flex-wrap mt-2 -mx-3">
        <div class="w-full px-3">
          <base-textarea
              tabindex="13"
              :textarea-id="'availability'"
              :value="''"
              placeholder="Enter your answer"
              name="availability"
              v-model="formData.availability"
              @blur="$v.formData.availability.$touch()"
              :validations="[
              {
                condition: $v.formData.availability.$error && !$v.formData.availability.required,
                text: validateMessage('required', { attr: 'This field'})
              }
            ]"
          >
            How often would you be available and wanting to teach classes? <span>*</span>
          </base-textarea>
        </div>
      </div>

      <!-- 10. Years in industry -->
      <div class="flex flex-wrap mt-2 -mx-3">
        <div class="w-full px-3">
          <base-textarea
              tabindex="14"
              :textarea-id="'years_in_industry'"
              :value="''"
              placeholder="Enter your answer"
              name="years_in_industry"
              v-model="formData.years_in_industry"
              @blur="$v.formData.years_in_industry.$touch()"
              :validations="[
              {
                condition: $v.formData.years_in_industry.$error && !$v.formData.years_in_industry.required,
                text: validateMessage('required', { attr: 'This field'})
              }
            ]"
          >
            Number of years in the industry, and any relevant education background and experience (min 3 years behind the chair) <span>*</span>
          </base-textarea>
        </div>
      </div>

      <!-- 11. R+CO experience -->
      <div class="flex flex-wrap mt-2 -mx-3">
        <div class="w-full px-3">
          <base-textarea
              tabindex="15"
              :textarea-id="'rco_experience'"
              :value="''"
              placeholder="Enter your answer"
              name="rco_experience"
              v-model="formData.rco_experience"
              @blur="$v.formData.rco_experience.$touch()"
              :validations="[
              {
                condition: $v.formData.rco_experience.$error && !$v.formData.rco_experience.required,
                text: validateMessage('required', { attr: 'This field'})
              }
            ]"
          >
            How long have you been working with R+CO and R+CO Bleu? (minimum 1 year) <span>*</span>
          </base-textarea>
        </div>
      </div>

      <!-- 12. Drivers license + car -->
      <div class="flex flex-wrap mt-2 -mx-3">
        <div class="w-full px-3">
          <base-select
              tabindex="16"
              name="drivers_license"
              :id="'drivers-license'"
              :options="driversLicenseOptions"
              placeholder="Select an option..."
              v-model="formData.drivers_license"
              :validations="[
              {
                condition: $v.formData.drivers_license.$error && !$v.formData.drivers_license.required,
                text: validateMessage('required', { attr: 'This field'})
              }
            ]"
          >
            Do you have a valid drivers license + car? <span>*</span>
          </base-select>
        </div>
      </div>

      <!-- 13. Open to traveling -->
      <div class="flex flex-wrap mt-2 -mx-3">
        <div class="w-full px-3">
          <base-textarea
              tabindex="17"
              :textarea-id="'open_to_travel'"
              :value="''"
              placeholder="Enter your answer"
              name="open_to_travel"
              v-model="formData.open_to_travel"
              @blur="$v.formData.open_to_travel.$touch()"
              :validations="[
              {
                condition: $v.formData.open_to_travel.$error && !$v.formData.open_to_travel.required,
                text: validateMessage('required', { attr: 'This field'})
              }
            ]"
          >
            Are you open to traveling outside of your home territory to teach classes? (driving up to 3 hours, flying possible if greater distance) <span>*</span>
          </base-textarea>
        </div>
      </div>

      <!-- 14. Education classes -->
      <div class="flex flex-wrap mt-2 -mx-3">
        <div class="w-full px-3">
          <base-textarea
              tabindex="18"
              :textarea-id="'education_classes'"
              :value="''"
              placeholder="Enter your answer"
              name="education_classes"
              v-model="formData.education_classes"
              @blur="$v.formData.education_classes.$touch()"
              :validations="[
              {
                condition: $v.formData.education_classes.$error && !$v.formData.education_classes.required,
                text: validateMessage('required', { attr: 'This field'})
              }
            ]"
          >
            Have you taken any education classes before - R+CO or otherwise? If so, please list or describe <span>*</span>
          </base-textarea>
        </div>
      </div>

      <div class="w-full px-3">
        <div class="text-right text-red mt-3">
          * Required Fields
        </div>
      </div>
      <div class="mt-2">
        <button-outline :type="'submit'" :color="'primary'" tabindex="19">
          Submit
        </button-outline>
      </div>
    </form>
  </div>
</template>

<script>
import {required, minLength, email} from 'vuelidate/lib/validators'
import BaseInput from './BaseInput.vue'
import BaseSelect from './BaseSelect.vue'
import BaseTextarea from './BaseTextarea.vue'
import ButtonOutline from './ButtonOutline.vue'
//import ValidationMessages from './ValidationMessages.vue'
import validateMessage from '../helpers/validateMessage.js'
import {validationMixin} from 'vuelidate'
import {isPhone} from '../helpers/validationRules.js'
import {httpClient} from '../helpers/httpClient.js'

export default {
  name: 'Join',
  components: {
    BaseInput,
    BaseSelect,
    BaseTextarea,
    ButtonOutline,
    //ValidationMessages
  },
  data() {
    return {
      submitted: false,
      driversLicenseOptions: [
        {label: 'Yes, both', value: 'Yes, both'},
        {label: 'Yes license, no car', value: 'Yes license, no car'},
        {label: 'No', value: 'No'}
      ],
      formData: {
        name: '',
        salon: '',
        address: '',
        address2: '',
        city: '',
        state: '',
        zip: '',
        referred_by: '',
        phone: '',
        email: '',
        instagram: '',
        salon_owner_approved: '',
        availability: '',
        years_in_industry: '',
        rco_experience: '',
        drivers_license: '',
        open_to_travel: '',
        education_classes: ''
      }
    }
  },
  validations() {
    return {
      formData: {
        name: {
          required,
          minLength: minLength(2)
        },
        salon: {
          required
        },
        address: {
          required
        },
        city: {
          required
        },
        state: {
          required
        },
        zip: {
          required
        },
        referred_by: {
          required
        },
        phone: {
          required,
          minLength: minLength(5),
          isPhone
        },
        email: {
          required,
          email,
          minLength: minLength(5)
        },
        instagram: {
          required
        },
        salon_owner_approved: {
          required
        },
        availability: {
          required
        },
        years_in_industry: {
          required
        },
        rco_experience: {
          required
        },
        drivers_license: {
          required
        },
        open_to_travel: {
          required
        },
        education_classes: {
          required
        }
      }
    }
  },
  methods: {
    submit() {
      if (this.$v.$invalid) {
        this.$v.$touch();
        this.scrollToError()
      } else {
        const payload = {
          name: this.formData.name,
          salon: this.formData.salon,
          address: this.formData.address,
          address2: this.formData.address2,
          city: this.formData.city,
          state: this.formData.state,
          zip: this.formData.zip,
          referred_by: this.formData.referred_by,
          phone: this.formData.phone,
          email: this.formData.email,
          instagram: this.formData.instagram,
          salon_owner_approved: this.formData.salon_owner_approved,
          availability: this.formData.availability,
          years_in_industry: this.formData.years_in_industry,
          rco_experience: this.formData.rco_experience,
          drivers_license: this.formData.drivers_license,
          open_to_travel: this.formData.open_to_travel,
          education_classes: this.formData.education_classes
        }

        httpClient.post(
          'https://wm.luxbp.com/api/r/automation/lbp-edu-form-application',
          {data: payload},
          { headers: { 'Content-Type': 'application/json' } }
        ).then(() => {
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({
            'event': 'edu-application-submission',
            'category': 'Form',
            'action': 'Submit',
            'label': 'EduApplicationFormSubmission'
          });

          if (typeof window.ga === 'function') {
            window.ga('send', {
              hitType: 'event',
              eventCategory: 'Form',
              eventAction: 'Submit',
              eventLabel: 'EduApplicationFormSubmission'
            });
          }
          this.submitted = true
          // window.scrollTo(0, 0)
        }).catch((error) => {
          console.error('Form submission error:', error)
          alert('There was an error submitting the form. Please try again.')
        })
      }
    },
    scrollToError() {
      setTimeout(() => {
        let errors = document.querySelectorAll('.errorMessage');
        if (errors && errors.length) {
          let top = window.pageYOffset + errors[0].getBoundingClientRect().top - 300;
          window.scrollTo({
            top: top,
            left: 0,
            behavior: 'smooth'
          })
        }
      }, 100)
    }
  },
  mixins: [validateMessage, validationMixin]
}
</script>


<style lang="scss">
img {
  border-width: 0;
  border-style: none;
}
</style>

<style lang="scss" scoped>
@import "../assets/css/output.css";

form {
  position: relative;
}

.content-title {
  text-align: left;
  font-size: 30px !important;
  position: relative;
  margin-top: 1rem;
  font-family: 'agenda-bold';
  text-transform: uppercase;
  margin-bottom: 0;

  &:first-child {
    margin-top: 0;
  }

  &:before {
    display: none;
    content: "";
    position: absolute;
    width: 25px;
    height: 2px;
    bottom: 0;
    left: 0;
    border: solid 1px;
  }
}
</style>
